import { MAPS, type MapDef } from "@neon-purge/shared";
import { lowSpec, weakHardware } from "../game/renderer.js";
import { MapPreview3D } from "../game/previews.js";

export interface MapPreviewHandle {
  loadMap: (map: MapDef) => void;
  showRandom: () => void;
  start: () => void;
  dispose: () => void;
}

/**
 * Renders a top-down schematic of a map onto a canvas — a cheap "minimap"
 * preview for the lobby. Boxes are drawn as rectangles (x→right, z→down),
 * tinted by their own color and brightened by height so buildings/towers
 * read as taller than ground cover. Spawns show as glowing dots.
 */
export function drawMapPreview(canvas: HTMLCanvasElement, map: MapDef): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;

  const { min, max } = map.bounds;
  const spanX = max[0] - min[0];
  const spanZ = max[2] - min[2];
  const pad = 8;
  const scale = Math.min((W - pad * 2) / spanX, (H - pad * 2) / spanZ);
  const offX = (W - spanX * scale) / 2;
  const offZ = (H - spanZ * scale) / 2;
  const toX = (x: number): number => offX + (x - min[0]) * scale;
  const toZ = (z: number): number => offZ + (z - min[2]) * scale;

  const day = map.theme === "day";
  ctx.fillStyle = day ? "#aeb9c4" : "#080b14";
  ctx.fillRect(0, 0, W, H);

  // sort by height so taller structures draw on top
  const boxes = [...map.boxes].sort((a, b) => a.s[1] - b.s[1]);
  for (const b of boxes) {
    const w = b.s[0] * scale;
    const h = b.s[2] * scale;
    const x = toX(b.p[0]) - w / 2;
    const z = toZ(b.p[2]) - h / 2;
    // skip the giant floor slab (drawn as background)
    if (b.s[0] > spanX * 0.9 && b.s[2] > spanZ * 0.9) continue;
    const tall = Math.min(1, b.s[1] / 8);
    ctx.globalAlpha = day ? 0.55 + tall * 0.4 : 0.4 + tall * 0.55;
    ctx.fillStyle = b.color ?? (day ? "#7f8894" : "#1a2136");
    ctx.fillRect(x, z, Math.max(1, w), Math.max(1, h));
    if (b.glow && b.s[1] > 3) {
      ctx.globalAlpha = 0.8;
      ctx.strokeStyle = b.glow;
      ctx.lineWidth = 1;
      ctx.strokeRect(x, z, Math.max(1, w), Math.max(1, h));
    }
  }

  // spawn dots
  ctx.globalAlpha = 1;
  for (const sp of map.spawns) {
    const elevated = sp.p[1] > 2;
    ctx.fillStyle = elevated ? "#ff2ec4" : "#00f0ff";
    ctx.beginPath();
    ctx.arc(toX(sp.p[0]), toZ(sp.p[2]), 2.4, 0, Math.PI * 2);
    ctx.fill();
  }

  // border
  ctx.globalAlpha = 1;
  ctx.strokeStyle = day ? "#3a4048" : "#00f0ff";
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
}

/** Warm the Play screen's 3D preview while the player sits on the main menu:
 *  builds the map they will land on (their saved pick, else the default) in an
 *  offscreen preview, compiles its programs ASYNC in the pooled preview GL
 *  context, then hands the context back to the pool. The first Play visit then
 *  finds the context created and the programs compiled — it used to pay both
 *  (~1.6 s on the menu-perf bench) in one synchronous block. Once per session. */
let previewWarmed = false;
export function warmMapPreview(): Promise<void> {
  if (previewWarmed) return Promise.resolve();
  previewWarmed = true;
  let id = "neo_island"; // playScreen's DEFAULT_MENU_MAP_ID
  try {
    const saved = (JSON.parse(localStorage.getItem("neonpurge.menu") ?? "{}") as { mapId?: string }).mapId;
    if (saved && MAPS[saved]) id = saved;
  } catch { /* corrupt prefs — default map */ }
  const map = MAPS[id];
  if (!map) return Promise.resolve();
  const c = document.createElement("canvas");
  c.width = 400; c.height = 225;
  const p = createMapPreview(c) as MapPreview3D;
  p.loadMap(map);
  return p.whenCompiled().then(() => p.parkWarm());
}

/** EVERY machine gets the real 3D preview — weaker hardware just renders it
 *  cheaper (no MSAA, fewer pixels, reduced frame rate) instead of being
 *  downgraded to a flat schematic. lowSpec() honors the "Full effects"
 *  override, so users can always force the top profile back on. */
export function createMapPreview(canvas: HTMLCanvasElement): MapPreviewHandle {
  if (lowSpec()) {
    // machine PROVED it can't hold frame rate — cheapest 3D profile: low res,
    // ~10fps spin, and NO wireframe edges (halves the object/draw count)
    return new MapPreview3D(canvas, { antialias: false, pixelRatio: 0.45, frameDivider: 6, noEdges: true });
  }
  if (weakHardware()) {
    // heuristically weak (few cores / low memory / integrated GPU). Slow
    // shader compilers (Intel UHD 630) choke on the wireframe edges too —
    // drop them and render the spin at ~20fps
    return new MapPreview3D(canvas, { antialias: false, pixelRatio: 0.6, frameDivider: 3, noEdges: true });
  }
  return new MapPreview3D(canvas);
}
